
import { useState } from 'react'
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'

import type {
  CreateCertificatePayload,
  TransferCertificatePayload,
  CancelTransferCertificatePayload,
  EditCertificatePayload,
} from 'store/certificates/types'

import { createApiCall } from 'services'

import type { Certificate } from 'types'
import type { ActivationTriggerValue } from '@repo/types'


type CreateCertificateFields = CreateCertificatePayload
type TransferCertificateFields = TransferCertificatePayload
type CancelTransferCertificateFields = CancelTransferCertificatePayload
type EditCertificateFields = EditCertificatePayload
type ActivateCertificateFields = { certificateId: string }
/**
 * VRG-528 — `activation_trigger: null` CLEARS the override so the certificate
 * inherits again (AC-6). It is a value, never an omission, so the field is
 * required here rather than optional.
 */
type SetActivationTriggerFields = {
  certificateId: string
  activation_trigger: ActivationTriggerValue | null
}

/**
 * VRG-591 — one flag at a time; `null` CLEARS the override so the certificate
 * inherits from its product and then the company.
 *
 * PARTIAL by construction — the caller sends only the key it is editing. The two
 * flags are independent, so naming both would let an edit to one restate a
 * possibly-stale copy of the other.
 */
type SetAccessSettingFields = {
  certificateId: string
} & (
  | {collect_email: boolean | null}
  | {collect_location: boolean | null}
)
type DeactivateCertificateFields = { certificateId: string }

type CreateCertificateWithoutScanSessionFields = {
  product_id: string
  tag_uids: string[]
}

type UseCertificateMutationsResult = [
  {
    create: (payload: CreateCertificateFields) => void
    createWithoutScanSession: (payload: CreateCertificateWithoutScanSessionFields) => void
    transfer: (payload: TransferCertificateFields) => void
    cancelTransfer: (payload: CancelTransferCertificateFields) => void
    edit: (payload: EditCertificateFields) => void
    /**
     * `options` forwards react-query's per-call `onSuccess` so a caller can SEQUENCE
     * a follow-up write instead of firing it alongside this one.
     *
     * Needed because the hook-level `onSuccess` refetches, so two concurrent
     * mutations produce two concurrent refetches and the one that lands last wins
     * the cache — which can be the one that read before the other write committed.
     * The row is correct and the page shows it stale. Sequencing is the only fix
     * that doesn't depend on which request happens to be quicker.
     */
    setActivationTrigger: (
      payload: SetActivationTriggerFields,
      options?: { onSuccess?: () => void },
    ) => void
    setAccessSetting: (payload: SetAccessSettingFields) => void
    activate: (payload: ActivateCertificateFields) => void
    deactivate: (payload: DeactivateCertificateFields) => void
  },
  {
    data?: Certificate | unknown
    isLoading: boolean
    isError: boolean
    isActivating: boolean
    isDeactivating: boolean
    isCreatingWithoutScanSession: boolean
    isSettingActivationTrigger: boolean
    isSettingAccessSetting: boolean
  }
]

type UseCertificateMutationsOptions = {
  onSuccess?: (data?: any) => void
  onError?: (error?: any) => void
}

export const useCertificateMutations = (options?: UseCertificateMutationsOptions): UseCertificateMutationsResult => {
  const queryClient = useQueryClient()
  const [data, setData] = useState<Certificate | unknown>()
  const [isError, setIsError] = useState(false)

  const invalidateQueries = async (keys: QueryKey[]) => {
    await Promise.all(
      keys.map(key =>
        queryClient.invalidateQueries({
          queryKey: key,
        })
      )
    )
  }

  const handleSuccess = async (responseData: unknown, invalidate: QueryKey[] = []) => {
    setData(responseData)
    setIsError(false)
    if (invalidate.length) {
      await invalidateQueries(invalidate)
    }
    options?.onSuccess?.(responseData)
  }

  const handleError = (error: unknown) => {
    setIsError(true)
    options?.onError?.(error)
  }

  const createMutation = useMutation({
    mutationKey: ['certificates', 'create'],
    mutationFn: async (payload: CreateCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/certificates/admin',
        data: payload,
      })

      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to create certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async data => {
      await handleSuccess(data, [['certificates']])
    },
    onError: handleError,
  })

  const createWithoutScanSessionMutation = useMutation({
    mutationKey: ['certificates', 'create-without-scan-session'],
    mutationFn: async (payload: CreateCertificateWithoutScanSessionFields) => {
      // Only allow in development mode
      //
      // TODO:: Find a better condition
      //
      // if (import.meta.env.VITE_ENVIRONMENT !== 'development') {
      //   throw new Error('This feature is only available in development mode')
      // }

      const response = await createApiCall({
        method: 'POST',
        url: '/certificates/admin/dev/create-without-scan-session',
        data: payload,
      })

      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to create certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async data => {
      await handleSuccess(data, [['certificates']])
    },
    onError: handleError,
  })

  /**
   * VRG-528 — its OWN route, not a field on the general update.
   *
   * `POST :id/activation-trigger` is gated by `billing_activation_settings`;
   * `POST :id` (the `edit` mutation below) is gated by `manage_certificate`, which
   * every role that can edit a description holds. Sending this field through that
   * one would route a billing-critical change past the permission chosen to
   * protect it, so the two must stay separate on the client as well as the server.
   *
   * Returns 200, not 201 — it updates rather than creates.
   */
  const setActivationTriggerMutation = useMutation({
    mutationKey: ['certificates', 'activation-trigger'],
    mutationFn: async ({
      certificateId,
      activation_trigger,
    }: SetActivationTriggerFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${certificateId}/activation-trigger`,
        data: { activation_trigger },
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to set activation trigger')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  /**
   * VRG-591 — its OWN route, for the same reason as the activation one above:
   * `POST :id/access-settings` is gated by `certificate_access_settings`, while
   * `POST :id` (the `edit` mutation below) carries the much broader
   * `manage_certificate`. Whether a visitor is made to hand over an email is a
   * consent decision, not a record edit, so the two must stay separate on the
   * client as well as the server.
   *
   * Returns 200, not 201 — it updates rather than creates.
   */
  const setAccessSettingMutation = useMutation({
    mutationKey: ['certificates', 'access-settings'],
    mutationFn: async ({certificateId, ...body}: SetAccessSettingFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${certificateId}/access-settings`,
        data: body,
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to set access setting')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  const editMutation = useMutation({
    mutationKey: ['certificates', 'edit'],
    mutationFn: async ({ certificateId, ...rest }: EditCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${certificateId}`,
        data: rest,
      })

      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to edit certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  const transferMutation = useMutation({
    mutationKey: ['certificates', 'transfer'],
    mutationFn: async ({ certificateId, ...rest }: TransferCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/transfer/admin/init/${certificateId}`,
        data: rest,
      })

      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to transfer certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  const cancelTransferMutation = useMutation({
    mutationKey: ['certificates', 'cancel-transfer'],
    mutationFn: async ({ certificateId }: CancelTransferCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/transfer/admin/cancel/${certificateId}`,
      })

      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to cancel transfer')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  const activateMutation = useMutation({
    mutationKey: ['certificates', 'activate'],
    mutationFn: async ({ certificateId }: ActivateCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${certificateId}/activate`,
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to activate certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  const deactivateMutation = useMutation({
    mutationKey: ['certificates', 'deactivate'],
    mutationFn: async ({ certificateId }: DeactivateCertificateFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/${certificateId}/deactivate`,
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to deactivate certificate')
      }

      return response.data
    },
    onMutate: () => setIsError(false),
    onSuccess: async (data, variables) => {
      await handleSuccess(data, [
        ['certificates'],
        ['certificates', 'detail', variables.certificateId],
      ])
    },
    onError: handleError,
  })

  return [
    {
      create: payload => createMutation.mutate(payload),
      createWithoutScanSession: payload => createWithoutScanSessionMutation.mutate(payload),
      transfer: payload => transferMutation.mutate(payload),
      cancelTransfer: payload => cancelTransferMutation.mutate(payload),
      edit: payload => editMutation.mutate(payload),
      setActivationTrigger: (payload, options) =>
        setActivationTriggerMutation.mutate(payload, options),
      setAccessSetting: payload => setAccessSettingMutation.mutate(payload),
      activate: payload => activateMutation.mutate(payload),
      deactivate: payload => deactivateMutation.mutate(payload),
    },
    {
      data,
      isLoading:
        createMutation.isPending ||
        createWithoutScanSessionMutation.isPending ||
        transferMutation.isPending ||
        cancelTransferMutation.isPending ||
        editMutation.isPending ||
        setActivationTriggerMutation.isPending ||
        setAccessSettingMutation.isPending ||
        activateMutation.isPending ||
        deactivateMutation.isPending,
      isError,
      isActivating: activateMutation.isPending,
      isDeactivating: deactivateMutation.isPending,
      isCreatingWithoutScanSession: createWithoutScanSessionMutation.isPending,
      isSettingActivationTrigger: setActivationTriggerMutation.isPending,
      isSettingAccessSetting: setAccessSettingMutation.isPending,
    },
  ]
}
